import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getCurrentBuser, type BuserData, updateCurrentBuserInfo, updateInitGuidedStep, type UpdateInitGuidedStepParams } from '../../api/auth/auth';
import { getShopList, type ShopInfo, type BrandInfo, type StepStatus, getS3UploadUrl as getS3UploadUrlApi, type GetS3UploadUrlParams } from '../../api/setting/setting';
import { install, buserAuth, type LoginParams } from '../../api/auth/auth';
import { setStorage } from '@/utils/cache';

interface AuthState {
  isAuthenticated: boolean;
  user: BuserData | null;
  shopList: ShopInfo[] | null;
  brandInfo: BrandInfo | null;
  shopifyToken: string | null;
  willlinksToken: string | null;
  token: string | null;
  pageMenuLocal: object | null;
  shopInfo: {
    shopId: string | null;
    shopName: string | null;
    shopUrl: string | null;
    shopOwner: string | null;
    shopEmail: string | null;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: true,
  user: null,
  shopList: null,
  brandInfo: null,
  shopifyToken: null,
  willlinksToken: null,
  token: null,
  pageMenuLocal: null,
  shopInfo: {
    shopId: null,
    shopName: null,
    shopUrl: null,
    shopOwner: null,
    shopEmail: null,
  },
  isLoading: false,
  error: null,
};

// 获取当前B端用户详情
export const fetchCurrentBuser = createAsyncThunk(
  'auth/fetchCurrentBuser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCurrentBuser();
      if (!response) {
        throw new Error('获取用户信息失败');
      }
      
      return response || null;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('获取用户信息过程中发生未知错误');
    }
  }
);

// 获取店铺列表详情
export const fetchShopList = createAsyncThunk<
  { shopList: ShopInfo[]; brandInfo: BrandInfo | null },
  { includeEmbed?: boolean } | undefined,
  { rejectValue: string }
>(
  'auth/fetchShopList',
  async (params, { rejectWithValue }) => {
    try {
      const includeEmbed = params?.includeEmbed ?? true;
      const response = await getShopList({ includeEmbed });
      
      if (!response) {
        throw new Error('获取店铺列表失败');
      }
      
      return {
        shopList: response?.shopList || [],
        brandInfo: response?.brandInfo || null
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('获取店铺列表过程中发生未知错误');
    }
  }
);

// 安装Shopify应用异步action
export const installShopifyApp = createAsyncThunk(
  'auth/installShopifyApp',
  async (installData: { shop: string; hmac: string; timestamp: string; host?: string | null; session?: string | null }, { rejectWithValue }) => {
    try {
      const resule = await install({
        shopDomain: installData.shop,
        hmac: installData.hmac,
        timestamp: Number(installData.timestamp),
        host: installData.host || undefined,
        session: installData.session || undefined
      });
      
      return resule;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('安装应用过程中发生未知错误');
    }
  }
);

// 更新B端用户工作语言
export const updateUserWorkLang = createAsyncThunk(
  'auth/updateUserWorkLang',
  async (workLang: string, { rejectWithValue }) => {
    try {
      const response = await updateCurrentBuserInfo({ workLang });
      
      if (!response) {
        throw new Error('更新用户语言失败');
      }
      
      return { workLang };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('更新用户语言过程中发生未知错误');
    }
  }
);

// 更新初始化引导步骤
export const updateGuidedSteps = createAsyncThunk(
  'auth/updateGuidedSteps',
  async (params: UpdateInitGuidedStepParams, { rejectWithValue }) => {
    try {
      const response = await updateInitGuidedStep(params);
      if (!response) {
        throw new Error('更新安装引导步骤失败');
      }
      
      return response.stepStatus || null;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('更新安装引导步骤过程中发生未知错误');
    }
  }
);

// 获取S3文件上传URL
export const getS3UploadUrl = createAsyncThunk(
  'auth/getS3UploadUrl',
  async (params: GetS3UploadUrlParams, { rejectWithValue }) => {
    try {
      const response = await getS3UploadUrlApi(params);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('获取S3文件上传URL过程中发生未知错误');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (params: LoginParams, { rejectWithValue }) => {
    try {
      const response = await buserAuth(params);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('登录过程中发生未知错误');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.shopList = null;
      state.brandInfo = null;
      state.willlinksToken = null;
      state.token = null;
      state.shopifyToken = null;
      state.shopInfo = {
        shopId: null,
        shopName: null,
        shopUrl: null,
        shopOwner: null,
        shopEmail: null,
      };
    },
    setWilllinksToken: (state, action: PayloadAction<string>) => {
      state.willlinksToken = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setShopifyToken: (state, action: PayloadAction<string>) => {
      state.shopifyToken = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setUser: (state, action: PayloadAction<BuserData>) => {
      state.user = action.payload;
    },
    setShopList: (state, action: PayloadAction<ShopInfo[]>) => {
      state.shopList = action.payload;
    },
    setBrandInfo: (state, action: PayloadAction<BrandInfo>) => {
      state.brandInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // installShopifyApp 相关处理
      .addCase(installShopifyApp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(installShopifyApp.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = true;
      })
      .addCase(installShopifyApp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // login 相关处理
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
      
        state.isAuthenticated = true;
        state.willlinksToken = action.payload.token;
        state.brandInfo = action.payload.brandInfo as BrandInfo;
        const defaultStepStatus = {
          dismiss: false,
          stepList: [
            {
              stepKey: 'stepKeySetStyleWidget',
              status: 1
            },
            {
              stepKey: 'stepKeyCustomizeHelpCenter',
              status: 1
            }
          ]
        }
        if (state.brandInfo?.stepStatus?.stepList) {
          state.brandInfo.stepStatus.stepList.forEach(item => {
            defaultStepStatus.stepList.forEach(value => {
              if (item.stepKey === value.stepKey) {
                value.status = item.status
              }
            })
          })
        }
        state.brandInfo.stepStatus = defaultStepStatus
        console.log('品牌信息', state.brandInfo)
        state.user = action.payload.buserInfo as BuserData;
        setStorage('language', state.user.workLang || 'en');
        state.pageMenuLocal = action.payload.pageMenuLocal as object;
        state.shopList = action.payload.shopList as ShopInfo[];
        console.log('❗登录成功', action.payload)
        if (import.meta.env.PUBLIC_NODE_ENV === 'production') {
          try {
            const userParams = {
              user_email: state.user.email,
              user_name: `${state.user.firstName} ${state.user.lastName}`,
              user_id: state.user.buserId
            }
            if (!window?.Willdesk && state.user.email) {
              const script = document.createElement('script');
              script.type = 'application/javascript';
              script.src = `https://static.willdesk.com/c/willdesk.min.js?v=${new Date().getTime()}`;
              document.body.appendChild(script);
              let load = false;
              
              Object.defineProperty(window, 'WilldeskLoad', {
                get() {
                  return load;
                },
                set(value) {
                  load = value;
                  if (load && window.Willdesk) {
                    window.Willdesk.boot(userParams)
                  }
                  return load;
                }
              })
            } else {
              window.Willdesk.boot(userParams)
            }
          } catch (error) {
            console.error('❌安装Willdesk失败', error)
          }
        }
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchCurrentBuser 相关处理
      .addCase(fetchCurrentBuser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentBuser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        setStorage('language', state.user.workLang || 'en');
      })
      .addCase(fetchCurrentBuser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchShopList 相关处理
      .addCase(fetchShopList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchShopList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.shopList = action.payload.shopList;
        state.brandInfo = action.payload.brandInfo;
      })
      .addCase(fetchShopList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateUserWorkLang 相关处理
      .addCase(updateUserWorkLang.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserWorkLang.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.workLang = action.payload.workLang;
        }
      })
      .addCase(updateUserWorkLang.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // updateGuidedSteps 相关处理
      .addCase(updateGuidedSteps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateGuidedSteps.fulfilled, (state, action) => {
        state.isLoading = false;
        // 如果有brandInfo，则更新其中的setpStatus
        if (state.brandInfo) {
          // 使用请求参数而不是响应值更新stepStatus
          state.brandInfo.stepStatus.stepList = action.meta.arg.guidedStepList;
        }
      })
      .addCase(updateGuidedSteps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // getS3UploadUrl 相关处理
      .addCase(getS3UploadUrl.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getS3UploadUrl.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getS3UploadUrl.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// 在slice外部定义selector
export const selectUser = (state: { auth: AuthState }) => {
  return state.auth.user
};

// 获取店铺是否开启嵌入
export const getShopEnabled = (state: { auth: AuthState }) => {
  const { shopList } = state.auth;
  const shop = shopify.config.shop;
  if (shopList?.length) {
    const shopInfo = shopList.find(shopItem => {
      return shopItem.shopDomain === shop;
    });
    return {
      embeddingSwitch: shopInfo?.embeddingSwitch,
      embeddingUrl: shopInfo?.embeddingUrl
    };
  }
  return false;
};

export const getShops = (state: { auth: AuthState }) => {
  return state.auth.shopList;
};

export const getBrandInfo = (state: { auth: AuthState }) => {
  return state.auth.brandInfo;
};

export const checkGuideStep = (stepKey: string) => {
  return (dispatch: any, getState: () => { auth: AuthState }) => {
    try {
      const state = getState();
      const brandInfo = state.auth.brandInfo;
      if (brandInfo?.stepStatus?.stepList) {
        const targetStep = brandInfo.stepStatus.stepList.find(step => {
          return step.stepKey === stepKey;
        });
        if (targetStep && targetStep.status !== 2) {
          const stepList = JSON.parse(JSON.stringify(brandInfo.stepStatus.stepList)).map(step => {
            if (step.stepKey === stepKey) {
              step.status = 2;
            }
            return step;
          });
          dispatch(updateGuidedSteps({
            dismiss: false,
            guidedStepList: [...stepList]
          }));
        }
      }
    } catch (e) {
      console.log(e)
    }
  };
};

export const {
  logout, 
  setWilllinksToken, 
  setToken, 
  setShopifyToken,
  setUser,
  setShopList,
  setBrandInfo
} = authSlice.actions;

export default authSlice.reducer;

